"use client"

import { useEffect, useState, useRef, useMemo } from "react"
import { usePathname, useRouter } from "next/navigation"

const COLUMN_COUNT = 16
const STAGGER_DELAY = 30 // ms between each column
const ANIMATION_DURATION = 400 // ms for each column animation
const CHARS_PER_COLUMN = 50
const SCRAMBLE_INTERVAL = 50 // ms between character updates

// Multi-language character sets for Matrix effect (balanced)
const CHAR_SETS = [
  "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン", // Japanese
  "的是不了在人有我他这个们中来上大为和国地到以说时要就出会可也你对生能而子那得于着", // Chinese (trimmed)
  "АБВГДЕЁЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ", // Russian
  "ABCDEFGHIJKLMNOPQRSTUVWXYZ", // Latin
  "0123456789", // Numbers
  "!@#$%^&*<>[]{}|/\\~`+=_-:;", // Special
  "█▓▒░▀▄■□▪▫▬▲►▼◄◊○●◘◙", // Blocks
]

// Pick a random character set first, then a random char from it (balanced distribution)
function getRandomChar(): string {
  const set = CHAR_SETS[Math.floor(Math.random() * CHAR_SETS.length)]
  return set[Math.floor(Math.random() * set.length)]
}

function MatrixColumn({
  isActive,
  columnIndex,
  phase,
}: {
  isActive: boolean
  columnIndex: number
  phase: string
}) {
  const [chars, setChars] = useState<string[]>([])
  const [visibleCount, setVisibleCount] = useState(0)
  const [mounted, setMounted] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Initialize on client only to avoid hydration mismatch
  useEffect(() => {
    setChars(Array.from({ length: CHARS_PER_COLUMN }, () => getRandomChar()))
    setVisibleCount(CHARS_PER_COLUMN)
    setMounted(true)
  }, [])

  // Scramble characters when active (during all transition phases)
  useEffect(() => {
    if (!mounted) return

    if (isActive && phase !== "idle") {
      intervalRef.current = setInterval(() => {
        setChars(prev =>
          prev.map((char, i) =>
            Math.random() < 0.3 ? getRandomChar() : char
          )
        )
      }, SCRAMBLE_INTERVAL)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isActive, phase, mounted])

  // Fade out characters progressively when entering (revealing page)
  useEffect(() => {
    if (!mounted) return

    if (phase === "enter") {
      const delay = (COLUMN_COUNT - 1 - columnIndex) * STAGGER_DELAY
      const fadeTimeout = setTimeout(() => {
        let count = CHARS_PER_COLUMN
        const fadeInterval = setInterval(() => {
          count -= 3
          if (count <= 0) {
            setVisibleCount(0)
            clearInterval(fadeInterval)
          } else {
            setVisibleCount(count)
          }
        }, 20)
      }, delay)
      return () => clearTimeout(fadeTimeout)
    } else if (phase === "exit" || phase === "covered") {
      setVisibleCount(CHARS_PER_COLUMN)
    }
  }, [phase, columnIndex, mounted])

  if (!mounted || (!isActive && phase === "idle")) return null

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-between overflow-hidden py-1">
      {chars.slice(0, visibleCount).map((char, j) => (
        <span
          key={j}
          className="font-mono text-xs leading-none"
          style={{
            color: j < 3
              ? `hsl(var(--primary) / ${0.9 - j * 0.2})`
              : `hsl(var(--primary) / ${Math.max(0.1, 0.4 - j * 0.01)})`,
            textShadow: j < 2 ? '0 0 8px hsl(var(--primary))' : 'none',
          }}
        >
          {char}
        </span>
      ))}
    </div>
  )
}

export function PageTransition({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const router = useRouter()
  const [isTransitioning, setIsTransitioning] = useState(true)
  const [phase, setPhase] = useState<"covered" | "exit" | "enter" | "idle">("covered")
  const [displayChildren, setDisplayChildren] = useState(children)
  const pendingHref = useRef<string | null>(null)
  const hasInitialized = useRef(false)
  const lastPathname = useRef(pathname)

  // Handle phase transitions and navigation
  useEffect(() => {
    if (phase === "exit") {
      const totalExitTime = ANIMATION_DURATION + (COLUMN_COUNT * STAGGER_DELAY) + 200
      const timer = setTimeout(() => {
        if (pendingHref.current) {
          if (pendingHref.current === pathname) {
            // Same page - just trigger enter without navigation
            pendingHref.current = null
            setPhase("enter")
          } else {
            router.push(pendingHref.current)
            pendingHref.current = null
          }
        }
      }, totalExitTime)
      return () => clearTimeout(timer)
    }
  }, [phase, router, pathname])

  // When pathname changes, update children and trigger enter
  useEffect(() => {
    if (hasInitialized.current && pathname !== lastPathname.current) {
      lastPathname.current = pathname
      setDisplayChildren(children)
      setPhase("enter")
    }
  }, [pathname, children])

  // Handle enter phase completion
  useEffect(() => {
    if (phase === "enter") {
      const totalEnterTime = ANIMATION_DURATION + (COLUMN_COUNT * STAGGER_DELAY) + 300
      const timer = setTimeout(() => {
        setPhase("idle")
        setIsTransitioning(false)
      }, totalEnterTime)
      return () => clearTimeout(timer)
    }
  }, [phase])

  // Listen for link clicks
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const anchor = target.closest("a")

      if (!anchor) return

      const href = anchor.getAttribute("href")
      if (!href) return

      if (href.startsWith("/") && !href.startsWith("//")) {
        // Same-page anchor on home (e.g. /#pricing) — let browser smooth-scroll
        if (href.startsWith("/#") && pathname === "/") return

        e.preventDefault()

        if (!isTransitioning) {
          setIsTransitioning(true)
          pendingHref.current = href
          setPhase("exit")
        }
      }
    }

    document.addEventListener("click", handleClick)
    return () => document.removeEventListener("click", handleClick)
  }, [pathname, isTransitioning])

  // Initial enter animation on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setPhase("enter")
      hasInitialized.current = true
    }, 150)
    return () => clearTimeout(timer)
  }, [])

  return (
    <>
      <div key={pathname}>
        {displayChildren}
      </div>

      {/* Transition overlay */}
      <div
        className="pointer-events-none fixed inset-0 z-[100] flex"
        aria-hidden="true"
      >
        {Array.from({ length: COLUMN_COUNT }).map((_, i) => (
          <div
            key={i}
            className="relative flex-1 bg-background"
            style={{
              transform: phase === "covered" || phase === "exit"
                ? "translateY(0%)"
                : "translateY(-100%)",
              transitionProperty: "transform",
              transitionDuration: phase === "covered" ? "0ms" : `${ANIMATION_DURATION}ms`,
              transitionTimingFunction: "cubic-bezier(0.76, 0, 0.24, 1)",
              transitionDelay: phase === "exit"
                ? `${i * STAGGER_DELAY}ms`
                : phase === "enter"
                  ? `${(COLUMN_COUNT - 1 - i) * STAGGER_DELAY}ms`
                  : "0ms",
            }}
          >
            {/* Matrix characters */}
            <MatrixColumn
              isActive={phase !== "idle"}
              columnIndex={i}
              phase={phase}
            />
          </div>
        ))}
      </div>
    </>
  )
}
